import { useEffect, useRef, useState } from 'react';
import { logError } from '../lib/logError';

/** How long the list and detail pages wait between refetches while something is still in progress. */
export const POLL_INTERVAL_MS = 15_000;

/** How long polling stays at `POLL_INTERVAL_MS` before it slows to `POLL_SLOW_INTERVAL_MS`. */
export const POLL_FAST_WINDOW_MS = 30 * 60_000;

/** The wait between refetches once polling has run for `POLL_FAST_WINDOW_MS`. */
export const POLL_SLOW_INTERVAL_MS = 60_000;

/**
 * Calls `callback` while `enabled` and the tab is visible, waiting `intervalMs` after each call settles.
 *
 * - The next wait starts only once the callback's promise settles, so polls never overlap.
 * - A hidden tab pauses the timer; becoming visible again fires once immediately, or as soon as an aborted call settles.
 * - The timer stops on unmount, whenever `enabled` turns false, and whenever `resetKey` changes.
 * - A new `intervalMs` restarts the timer at that interval.
 *
 * Each call gets its own `AbortSignal`.
 * The signal aborts on unmount, when `enabled` turns false, when `resetKey` or `intervalMs` changes, and when the tab is hidden.
 * The callback passes it to its requests so they, and any retries they have not started, stop.
 */
const useVisiblePolling = (
  callback: (signal: AbortSignal) => unknown,
  intervalMs: number,
  enabled: boolean,
  resetKey?: unknown
): void => {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey restarts polling and aborts the running call without being read
  useEffect(() => {
    if (!enabled) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    /* The running call's controller, kept until the call settles even once aborted */
    let run: AbortController | null = null;
    let tickOnSettle = false;
    let active = true;

    const stop = () => {
      if (timer != null) clearTimeout(timer);
      timer = null;
    };
    const schedule = () => {
      if (active && timer == null && run == null && document.visibilityState !== 'hidden')
        timer = setTimeout(tick, intervalMs);
    };
    function tick() {
      timer = null;
      if (run != null) return;
      const controller = new AbortController();
      run = controller;
      /* The async wrapper turns a synchronous throw into a rejection */
      (async () => callbackRef.current(controller.signal))()
        .catch(error => {
          if (!controller.signal.aborted) logError('poll', error);
        })
        .finally(() => {
          run = null;
          const resume = tickOnSettle && active && document.visibilityState !== 'hidden';
          tickOnSettle = false;
          if (resume) tick();
          else schedule();
        });
    }
    const onVisibilityChange = () => {
      stop();
      if (document.visibilityState === 'hidden') {
        tickOnSettle = false;
        run?.abort();
        return;
      }
      if (run == null) tick();
      else if (run.signal.aborted) tickOnSettle = true;
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      active = false;
      stop();
      run?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, intervalMs, resetKey]);
};

/**
 * The interval to poll at: `POLL_INTERVAL_MS` for the first `POLL_FAST_WINDOW_MS` of continuous polling, then `POLL_SLOW_INTERVAL_MS`.
 *
 * proxima-audiences can leave a destination in progress indefinitely, so an open page must not poll fast forever.
 *
 * The fast window starts again when:
 * - the hook mounts,
 * - `enabled` turns true,
 * - `resetKey` changes.
 */
export const useBoundedPollInterval = (
  enabled: boolean,
  resetKey?: unknown
): { intervalMs: number } => {
  const [slow, setSlow] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey restarts the fast window without being read
  useEffect(() => {
    setSlow(false);
    if (!enabled) return undefined;
    const timer = setTimeout(() => setSlow(true), POLL_FAST_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [enabled, resetKey]);

  return { intervalMs: slow ? POLL_SLOW_INTERVAL_MS : POLL_INTERVAL_MS };
};

export default useVisiblePolling;
